import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { MetricCard } from "@/components/MetricCard";
import SimulatorStatusBadge from "@/components/SimulatorStatusBadge";
import DeviceStatusIndicator from "@/components/DeviceStatusIndicator";
import { DASHBOARD } from "@/constants/testIds";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Battery, Droplets, ArrowDownToLine, ArrowUpFromLine, Activity, AlertCircle } from "lucide-react";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
}

export default function Dashboard() {
  const { current, currentId } = useProject();
  const [sensors, setSensors] = useState([]);
  const [readings, setReadings] = useState([]);
  const [latest, setLatest] = useState([]);
  const [water, setWater] = useState(null);
  const [solenoids, setSolenoids] = useState([]);

  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [sRes, rRes, lRes, wRes, solRes] = await Promise.all([
          api.get(`/projects/${currentId}/sensors`),
          api.get(`/projects/${currentId}/readings`, { params: { limit: 400 } }),
          api.get(`/projects/${currentId}/latest`),
          api.get(`/projects/${currentId}/water`),
          api.get(`/projects/${currentId}/solenoids`),
        ]);
        if (cancelled) return;
        setSensors(sRes.data);
        setReadings(rRes.data);
        setLatest(lRes.data);
        setWater(wRes.data);
        setSolenoids(solRes.data);
      } catch (e) {
        // silent
      }
    };
    fetchAll();
    const id = setInterval(fetchAll, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [currentId]);

  const sensorById = useMemo(() => Object.fromEntries(sensors.map((s) => [s.id, s])), [sensors]);

  const soilLatest = latest.filter((l) => l.sensor_type === "soil_moisture");
  const avgSoil = soilLatest.length ? soilLatest.reduce((a, b) => a + b.value, 0) / soilLatest.length : 0;
  const flowIn = latest.find((l) => l.sensor_type === "flow_in")?.value || 0;
  const flowOut = latest.find((l) => l.sensor_type === "flow_out")?.value || 0;
  // SoC: only trust PZEM readings that carry `metrics` (real ESP32 push contains
  // V/A/W/Wh; simulator writes value-only). This prevents legacy simulator readings
  // (e.g. stuck at ~78%) from ever surfacing on the dashboard once real hardware is online.
  const pzemAll = latest.filter((l) => l.sensor_type === "pzem");
  const pzemReal = pzemAll.filter((l) => l.metrics && typeof l.metrics === "object" && "voltage" in l.metrics);
  const socAvailable = pzemReal.length > 0;
  // Voltage-based SoC (VRLA calibration). Fallback range 11.4–13.5 V matches backend default.
  const vMin = current?.battery_voltage_min ?? 11.4;
  const vMax = current?.battery_voltage_max ?? 13.5;
  const vSpan = Math.max(0.01, vMax - vMin);
  const socFromVoltage = (v) => Math.max(0, Math.min(100, ((Number(v) || 0) - vMin) / vSpan * 100));
  // Recompute from `metrics.voltage` on-the-fly so legacy readings (stored `.value` from
  // the old energy-based formula) are corrected on the client without DB migration.
  const soc = socAvailable
    ? Math.min(...pzemReal.map((l) => socFromVoltage(l.metrics.voltage)))
    : 0;

  // Build soil moisture timeseries (multi-line).
  // Build flow chart data — soil chart was removed from dashboard per user request; live
  // soil moisture ref value is now shown inline in Solenoid Control → Otomatis tab instead.
  const flowData = useMemo(() => {
    const flowSensors = sensors.filter((s) => s.type === "flow_in" || s.type === "flow_out");
    const buckets = new Map();
    for (const r of readings) {
      if (r.sensor_type !== "flow_in" && r.sensor_type !== "flow_out") continue;
      const t = fmtTime(r.timestamp);
      if (!buckets.has(t)) buckets.set(t, { time: t });
      // Key by sensor_id so multi flow sensors don't overwrite each other
      buckets.get(t)[r.sensor_id] = r.value;
    }
    // Same fix as soilData: readings sorted DESC → slice from HEAD, then reverse for LTR chart.
    const arr = Array.from(buckets.values()).slice(0, 20).reverse();
    return { data: arr, sensors: flowSensors };
  }, [readings, sensors]);

  // ---- Dynamic charts for extended sensors (DHT22/BME280/BH1750/UV/NPK/etc.) ----
  // Group sensors by type, build time series per group.
  const CORE_TYPES = new Set(["soil_moisture", "flow_in", "flow_out", "solenoid", "pzem"]);
  const TYPE_LABEL = {
    dht22: "DHT22 (Temp)", bme280: "BME280 (Temp)", bmp280: "BMP280 (Pressure)",
    bh1750: "BH1750 (Lux)", ds18b20: "DS18B20 (Temp)", gps_neo6m: "GPS (Lat)",
    si1145: "SI1145 (UV)", as7262: "AS7262 (Spec)", as7265x: "AS7265x (Spec)",
    sht31: "SHT31 (Temp)", npk: "NPK (ppm)", ph_soil: "pH Tanah",
    bpw34: "BPW34 (W/m²)", pyranometer: "Pyranometer (W/m²)",
    wind_speed: "Wind Speed (m/s)", generic: "Custom Sensor",
  };
  const extendedSensors = useMemo(
    () => sensors.filter((s) => !CORE_TYPES.has(s.type)),
    [sensors]
  );
  const extendedChartsByType = useMemo(() => {
    const groups = {};
    for (const s of extendedSensors) {
      (groups[s.type] = groups[s.type] || []).push(s);
    }
    const result = [];
    for (const [type, slist] of Object.entries(groups)) {
      const sensorIds = new Set(slist.map((x) => x.id));
      const buckets = new Map();
      for (const r of readings) {
        if (!sensorIds.has(r.sensor_id)) continue;
        const t = fmtTime(r.timestamp);
        if (!buckets.has(t)) buckets.set(t, { time: t });
        buckets.get(t)[r.sensor_id] = r.value;
      }
      const arr = Array.from(buckets.values()).slice(0, 30).reverse();
      result.push({ type, label: TYPE_LABEL[type] || type, sensors: slist, data: arr, unit: slist[0]?.unit || "" });
    }
    return result;
  }, [readings, extendedSensors]);

  if (!currentId) {
    return (
      <div className="border border-dashed border-border rounded-lg p-12 text-center">
        <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground" />
        <p className="mt-3 text-muted-foreground">Belum ada proyek. Mintalah Admin untuk membuatnya.</p>
      </div>
    );
  }

  const alerts = [];
  if (water && water.daily_out_l > water.daily_limit_l) {
    alerts.push({ level: "warn", msg: `Debit harian (${water.daily_out_l.toFixed(1)} L) melampaui batas ${water.daily_limit_l} L` });
  }
  if (soc > 0 && soc < 30) alerts.push({ level: "warn", msg: `Baterai surya rendah: ${soc.toFixed(0)}%` });
  if (avgSoil > 0 && avgSoil < 25) alerts.push({ level: "warn", msg: `Kelembapan tanah rendah: rata-rata ${avgSoil.toFixed(0)}%` });

  return (
    <div>
      <PageHeader
        overline={current?.location || "Lahan aktif"}
        title={current?.name || "Dashboard"}
        description={current?.description || "Monitoring kondisi lahan secara real-time."}
        actions={
          <div className="flex items-center gap-3">
            <DeviceStatusIndicator projectId={currentId} />
            <SimulatorStatusBadge projectId={currentId} />
            <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
              <span className="status-dot bg-primary" />
              Live · update tiap 5 detik
            </div>
          </div>
        }
      />

      {alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 border border-accent/40 bg-accent/5 rounded-md">
              <AlertCircle className="w-4 h-4 text-accent" />
              <span className="text-sm">{a.msg}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          testId={DASHBOARD.metricSoc}
          overline="Solar SoC"
          value={socAvailable ? soc.toFixed(0) : "—"}
          unit={socAvailable ? "%" : ""}
          hint={socAvailable ? "State of Charge baterai panel surya" : "Menunggu data PZEM ESP32…"}
          icon={Battery}
        />
        <MetricCard
          testId={DASHBOARD.metricWaterAvail}
          overline="Air Tersedia"
          value={water ? water.available_l.toFixed(1) : "0"}
          unit="L"
          hint={`Masuk ${water?.total_in_l.toFixed(1) || 0} L · Keluar ${water?.total_out_l.toFixed(1) || 0} L`}
          icon={Droplets}
          accent="primary"
        />
        <MetricCard
          testId={DASHBOARD.metricFlowIn}
          overline="Debit Masuk"
          value={flowIn.toFixed(2)}
          unit="L/menit"
          icon={ArrowDownToLine}
        />
        <MetricCard
          testId={DASHBOARD.metricFlowOut}
          overline="Debit Keluar"
          value={flowOut.toFixed(2)}
          unit="L/menit"
          icon={ArrowUpFromLine}
          accent="accent"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="border border-border rounded-lg bg-card p-6" data-testid={DASHBOARD.chartFlow}>
          <div className="overline text-muted-foreground">Flowmeter</div>
          <h3 className="font-display text-lg font-semibold mt-1 mb-4">
            Debit Air ({flowData.sensors.length} sensor · L/menit)
          </h3>
          {flowData.sensors.length === 0 ? (
            <div className="text-sm text-muted-foreground italic h-[260px] flex items-center justify-center border border-dashed border-border rounded-md">
              Belum ada sensor flowmeter — tambahkan di Sensor Manajemen.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={flowData.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {flowData.sensors.map((s, idx) => (
                  <Bar
                    key={s.id}
                    dataKey={s.id}
                    name={`${s.name} (${s.type === "flow_in" ? "IN" : "OUT"})`}
                    fill={`hsl(var(--chart-${(idx % 5) + 1}))`}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Solenoid Valve Status */}
      <div className="border border-border rounded-lg bg-card p-6 mb-6" data-testid="dashboard-solenoid-status">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-muted-foreground">Solenoid Valve</div>
            <h3 className="font-display text-lg font-semibold mt-1">
              Status Valve ({solenoids.length} valve · {solenoids.filter((s) => s.is_on).length} ON)
            </h3>
          </div>
        </div>
        {solenoids.length === 0 ? (
          <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded-md p-6 text-center">
            Belum ada valve solenoid — tambahkan di Sensor Manajemen.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {solenoids.map((sol) => {
              const sn = sensors.find((x) => x.id === sol.sensor_id);
              const modeLabel = { manual: "Manual", schedule: "Jadwal", auto: "Otomatis" }[sol.mode] || sol.mode;
              return (
                <div
                  key={sol.id}
                  data-testid={`dashboard-valve-${sol.id}`}
                  data-on={sol.is_on}
                  className={`border rounded-md p-3 transition-colors ${
                    sol.is_on
                      ? "border-emerald-500/40 bg-emerald-500/5"
                      : "border-border bg-background/40"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-medium truncate flex-1" title={sn?.name || sol.sensor_id.slice(0, 8)}>
                      {sn?.name || `Valve ${sol.sensor_id.slice(0, 6)}`}
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider ${
                        sol.is_on
                          ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {sol.is_on ? "ON" : "OFF"}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <span className="uppercase">{modeLabel}</span>
                    {sn?.pin && <span>· {sn.pin}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="dashboard-extended-charts">
        {extendedChartsByType.length === 0 && (
          <div className="hidden" data-testid="extended-charts-empty" />
        )}
        {extendedChartsByType.map((g) => (
            <div key={g.type} className="border border-border rounded-lg bg-card p-6" data-testid={`chart-ext-${g.type}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline text-muted-foreground">{g.type}</div>
                  <h3 className="font-display text-lg font-semibold mt-1">
                    {g.label} <span className="text-sm text-muted-foreground font-normal">· {g.sensors.length} sensor</span>
                  </h3>
                </div>
                <div className="text-xs text-muted-foreground">{g.unit}</div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={g.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  {g.sensors.map((s, idx) => (
                    <Line
                      key={s.id}
                      type="monotone"
                      dataKey={s.id}
                      name={s.name}
                      stroke={`hsl(var(--chart-${(idx % 5) + 1}))`}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
      </div>

      <div className="border border-border rounded-lg bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-primary" />
          <h3 className="font-display text-lg font-semibold">Status sensor terkini</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sensors.filter((s) => s.type !== "solenoid").map((s) => {
            const v = latest.find((l) => l.sensor_id === s.id);
            return (
              <div key={s.id} className="border border-border rounded-md p-3">
                <div className="text-xs text-muted-foreground">{s.name}</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-display text-xl font-bold">{v?.value?.toFixed(2) ?? "—"}</span>
                  <span className="text-xs text-muted-foreground">{s.unit}</span>
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground tracking-wider uppercase">{s.type}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
